import React, { useState, useEffect, useRef } from 'react';
import { Typography, Card } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { primaryShades, variantTokens, greyScaleTokens } from '../../../theme/theme';
import Chart from 'chart.js/auto';
import CustomTooltip from '../Modals/CustomTooltip';

const { Text } = Typography;

// Costanti dei colori
const PRIMARY = variantTokens.primary;
const PRIMARY_300 = primaryShades.primary300;
const PRIMARY_700 = primaryShades.primary700;
const SECONDARY = variantTokens.secondary;
const WHITE = variantTokens.white;
const GREY_200 = greyScaleTokens.grey200;
const SHADOW_COLOR = 'rgba(0,0,0,0.05)';

// Componente per la riga di bottoni superiori
const DevicesSummary = ({ data, activeTab, onTabChange }) => {
    return (
        <div style={{
            display: 'flex',
            marginBottom: 'clamp(0.75rem, 3vw, 1.25rem)',
            width: '100%',
            borderRadius: 'clamp(0.25rem, 2vw, 0.5rem)',
            overflow: 'hidden',
            boxShadow: `clamp(0rem, 0.1vw, 0rem) clamp(0.0625rem, 0.5vw, 0.125rem) clamp(0.3125rem, 2.5vw, 0.625rem) ${SHADOW_COLOR}`
        }}>
            {data.map((item, index) => {
                const isActive = activeTab === item.key;
                const isFirst = index === 0;
                const isLast = index === data.length - 1;

                return (
                    <div
                        key={item.key}
                        onClick={() => onTabChange(item.key)}
                        style={{
                            flex: 1,
                            padding: 'clamp(0.9375rem, 4vw, 1.875rem) clamp(0.625rem, 3vw, 1.25rem)',
                            cursor: 'pointer',
                            backgroundColor: isActive ? PRIMARY_300 : WHITE,
                            color: isActive ? WHITE : PRIMARY_300,
                            borderRight: isLast ? 'none' : `clamp(0.0625rem, 0.2vw, 0.0625rem) solid ${GREY_200}`,
                            border: `clamp(0.0625rem, 0.2vw, 0.0625rem) solid ${GREY_200}`,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.3s ease',
                            textAlign: 'center',
                            borderTopLeftRadius: isFirst ? 'clamp(0.25rem, 2vw, 0.5rem)' : '0',
                            borderBottomLeftRadius: isFirst ? 'clamp(0.25rem, 2vw, 0.5rem)' : '0',
                            borderTopRightRadius: isLast ? 'clamp(0.25rem, 2vw, 0.5rem)' : '0',
                            borderBottomRightRadius: isLast ? 'clamp(0.25rem, 2vw, 0.5rem)' : '0',
                        }}
                    >
                        <div style={{
                            fontSize: 'clamp(1.5rem, 5vw, 2.25rem)',
                            fontWeight: 'bold',
                            color: isActive ? WHITE : PRIMARY_300
                        }}>
                            {item.count}
                        </div>
                        <div style={{
                            fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
                            fontWeight: 'bold',
                            marginTop: 'clamp(0.15625rem, 1vw, 0.3125rem)',
                            color: isActive ? WHITE : PRIMARY_300
                        }}>
                            {item.label}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

// Componente per il grafico a barre
const BarChart = ({ data, activeTab }) => {
    const chartRef = useRef(null);
    const chartInstanceRef = useRef(null);
    const containerRef = useRef(null);

    useEffect(() => {
        if (chartInstanceRef.current) {
            chartInstanceRef.current.destroy();
        }

        if (!chartRef.current) return;

        const ctx = chartRef.current.getContext('2d');

        // Dati per il grafico a barre
        const labels = data.barChart.labels;
        const values = data.barChart.values;

        chartInstanceRef.current = new Chart(ctx, {
            type: 'bar',
            data: {
                labels: labels,
                datasets: [{
                    label: 'Device Usage',
                    data: values,
                    backgroundColor: PRIMARY,
                    borderWidth: 0,
                    borderRadius: 4,
                    barPercentage: 0.5,
                    maxBarThickness: 35
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        display: false,
                    },
                    tooltip: {
                        backgroundColor: PRIMARY_700,
                        padding: 10,
                        titleColor: '#fff',
                        bodyColor: '#fff',
                        titleFont: {
                            size: 14,
                            weight: 'bold'
                        },
                        bodyFont: {
                            size: 12
                        },
                        callbacks: {
                            label: function (context) {
                                return `Count: ${context.raw}`;
                            }
                        }
                    }
                },
                scales: {
                    y: {
                        beginAtZero: true,
                        grid: {
                            display: true,
                            color: '#e0e0e0',
                            drawBorder: false,
                        },
                        ticks: {
                            font: {
                                size: 11
                            },
                            color: '#666',
                            padding: 5
                        },
                    },
                    x: {
                        grid: {
                            display: false,
                            drawBorder: false
                        },
                        ticks: {
                            font: {
                                size: 11
                            },
                            color: '#666',
                            padding: 5
                        }
                    }
                }
            }
        });

        return () => {
            if (chartInstanceRef.current) {
                chartInstanceRef.current.destroy();
            }
        };
    }, [data, activeTab]);

    return (
        <Card
            styles={{ body: { padding: 'clamp(0.75rem, 3vw, 1rem)', height: 'clamp(18.75rem, 50vh, 25rem)' } }}
            style={{ marginBottom: 'clamp(0.75rem, 3vw, 1.25rem)', height: 'clamp(18.75rem, 50vh, 25rem)' }}
        >
            <div ref={containerRef} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <div style={{ marginBottom: 'clamp(0.5rem, 2vw, 0.75rem)' }}>
                    <Text strong>Device Usage Distribution</Text>
                    <CustomTooltip title="Shows the distribution of device usage across different categories">
                        <InfoCircleOutlined style={{ marginLeft: 'clamp(0.25rem, 1vw, 0.5rem)' }} />
                    </CustomTooltip>
                </div>
                <div style={{ flexGrow: 1, position: 'relative', minHeight: '0' }}>
                    <canvas ref={chartRef}></canvas>
                </div>
            </div>
        </Card>
    );
};

// Componente per il grafico a ciambella
const DoughnutChart = ({ data, activeTab }) => {
    const chartRef = useRef(null);
    const chartInstanceRef = useRef(null);
    const containerRef = useRef(null);

    useEffect(() => {
        if (chartInstanceRef.current) {
            chartInstanceRef.current.destroy();
        }

        if (!chartRef.current) return;

        const ctx = chartRef.current.getContext('2d');

        // Dati per il grafico a ciambella
        chartInstanceRef.current = new Chart(ctx, {
            type: 'doughnut',
            data: {
                labels: ['Mobile Device', 'Desktop Device'],
                datasets: [{
                    data: [data.pieChart.mobile, data.pieChart.desktop],
                    backgroundColor: [PRIMARY, SECONDARY],
                    borderWidth: 0,
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                cutout: '60%',
                plugins: {
                    legend: {
                        position: 'bottom',
                        labels: {
                            padding: 15,
                            usePointStyle: true,
                            pointStyle: 'circle',
                            font: {
                                size: 12
                            }
                        }
                    },
                    tooltip: {
                        backgroundColor: PRIMARY_700,
                        padding: 10,
                        titleColor: '#fff',
                        bodyColor: '#fff',
                        titleFont: {
                            size: 14,
                            weight: 'bold'
                        },
                        bodyFont: {
                            size: 12
                        }
                    }
                }
            }
        });

        return () => {
            if (chartInstanceRef.current) {
                chartInstanceRef.current.destroy();
            }
        };
    }, [data, activeTab]);

    return (
        <Card
            styles={{ body: { padding: 'clamp(0.75rem, 3vw, 1rem)', height: 'clamp(18.75rem, 50vh, 25rem)' } }}
            style={{ height: 'clamp(18.75rem, 50vh, 25rem)' }}
        >
            <div ref={containerRef} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <div style={{ marginBottom: 'clamp(0.5rem, 2vw, 0.75rem)' }}>
                    <Text strong>Device Type Distribution</Text>
                    <CustomTooltip title="Shows the distribution between mobile and desktop devices">
                        <InfoCircleOutlined style={{ marginLeft: 'clamp(0.25rem, 1vw, 0.5rem)' }} />
                    </CustomTooltip>
                </div>
                <div style={{ flexGrow: 1, position: 'relative', minHeight: '0' }}>
                    <canvas ref={chartRef}></canvas>
                </div>
            </div>
        </Card>
    );
};

const DoubleChartsBox = () => {
    // Dati statistici per i bottoni
    const tabsData = [
        { key: 'ALL', label: 'ALL', count: 9857 },
        { key: 'MOBILE', label: 'MOBILE', count: 5893 },
        { key: 'DESKTOP', label: 'DESKTOP', count: 3964 }
    ];

    // Dati di esempio per i grafici
    const chartsData = {
        'ALL': {
            barChart: {
                labels: ['Chrome', 'Safari', 'Firefox', 'Edge', 'Others'],
                values: [5231, 2765, 1098, 612, 151]
            },
            pieChart: {
                mobile: 5893,
                desktop: 3964
            }
        },
        'MOBILE': {
            barChart: {
                labels: ['Chrome', 'Safari', 'Firefox', 'Samsung', 'Others'],
                values: [2841, 2312, 423, 287, 30]
            },
            pieChart: {
                mobile: 5893,
                desktop: 0
            }
        },
        'DESKTOP': {
            barChart: {
                labels: ['Chrome', 'Safari', 'Firefox', 'Edge', 'Others'],
                values: [2390, 453, 675, 325, 121]
            },
            pieChart: {
                mobile: 0,
                desktop: 3964
            }
        }
    };

    // Stato per tenere traccia della scheda attiva
    const [activeTab, setActiveTab] = useState(tabsData[0].key);

    // Funzione per gestire il cambio di scheda
    const handleTabChange = (tabKey) => {
        setActiveTab(tabKey);
    };

    return (
        <div>
            <DevicesSummary data={tabsData} activeTab={activeTab} onTabChange={handleTabChange} />
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(clamp(20rem, 40vw, 30rem), 1fr))',
                gap: 'clamp(0.75rem, 3vw, 1.25rem)'
            }}>
                <BarChart data={chartsData[activeTab]} activeTab={activeTab} />
                <DoughnutChart data={chartsData[activeTab]} activeTab={activeTab} />
            </div>
        </div>
    );
};

export default DoubleChartsBox;